import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import TopNav from "../../commonUI/TopHeader/TopHeader";
import SearchFilterBar from "../../commonUI/SearchFilterBar/SearchFilterBar";
import DataTable from "../../commonUI/DataTable/DataTable";
import Pagination from "../../commonUI/Pagination/Pagination";
import {
  serverUrl,
  userImagesFolder,
  thumbSuffix,
  imageFormat,
  defaultAvatarImage,
} from "../../../config/env";
import {
  CONVERSATION_SENDER_OPTIONS,
  CONVERSATIONS_COLUMNS,
  STATIC_CONVERSATIONS,
} from "../../../config/constants";

const Conversations = ({ userDetails }) => {
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch]           = useState("");
  const [senderFilter, setSenderFilter] = useState("any");
  const [dateRange, setDateRange]     = useState({ start: null, end: null });
  const [pageSize, setPageSize]       = useState(15);
  const [totalPages, setTotalPages]   = useState(1);
  const [page, setPage]               = useState(1);
  const [inputValue, setInputValue]   = useState(1);

  const handleSearch = useCallback(() => {
    setSearch(searchValue);
    setPage(1);
  }, [searchValue]);

  const handleReset = () => {
    setSearchValue("");
    setSearch("");
    setSenderFilter("any");
    setDateRange({ start: null, end: null });
    setPage(1);
  };

  const getAvatarSrc = useCallback((conversation) => {
    if (conversation?.client?.avatar && conversation?.client?.avatar !== "") {
      return `${serverUrl}${userImagesFolder}/${conversation?.client?.shortId}/${conversation?.client?.avatar}${thumbSuffix}${imageFormat}`;
    }
    return `../img/${defaultAvatarImage}${imageFormat}`;
  }, []);

  const handleViewConversation = useCallback(
    (conversation) => {
      navigate(`/conversation/user/${conversation.id}`, {
        state: {
          conversation,
          conversationType: "user-user",
          activeTab: "conversations",
        },
      });
    },
    [navigate]
  );

  const renderRow = useCallback(
    (conversation, index) => (
      <tr key={index}>
        <td>
          <div className="d-flex align-items-center g-12">
            <img
              src={getAvatarSrc(conversation)}
              alt="avatar"
              className="avatar-pic"
              onError={(e) => { e.target.src = defaultAvatarImage; }}
            />
            <div className="text-capitalize">
              {conversation?.client?.name}
              <br />
              <span className="text-sm text-gray-600">
                {conversation?.client?.shortId}
              </span>
            </div>
          </div>
        </td>
        <td>
          <div
            className={`message-container ${
              conversation?.userType === "client"
                ? "message-client"
                : conversation?.userType === "driver"
                ? "message-driver ml-32"
                : "message-admin"
            }`}
          >
            <p>{conversation?.message || "-"}</p>
          </div>
        </td>
        <td>
          <div className="d-flex align-items-center g-12">
            <img
              src={getAvatarSrc(conversation)}
              alt="avatar"
              className="avatar-pic"
              onError={(e) => { e.target.src = defaultAvatarImage; }}
            />
            <div className="text-capitalize">
              {conversation?.driver?.name}
              <br />
              <span className="text-sm text-gray-600">
                {conversation?.driver?.shortId}
              </span>
            </div>
          </div>
        </td>
        <td>
          {conversation?.sentAt?.date}
          <br />
          <span className="text-sm text-gray-600">
            {conversation?.sentAt?.time}
          </span>
        </td>
        <td>
          <button
            type="button"
            className="custom-btn"
            onClick={() => handleViewConversation(conversation)}
          >
            View
          </button>
        </td>
      </tr>
    ),
    [getAvatarSrc, handleViewConversation]
  );

  const filters = useMemo(
    () => [
      {
        type:        "search",
        key:         "search",
        label:       "Search",
        value:       searchValue,
        onChange:    setSearchValue,
        placeholder: "Search...",
        onSubmit:    handleSearch,
      },
      {
        type:        "select",
        key:         "sender",
        label:       "Sender",
        value:       senderFilter,
        onChange:    setSenderFilter,
        placeholder: "Select Sender",
        options:     CONVERSATION_SENDER_OPTIONS,
      },
      {
        type:        "dateRange",
        key:         "dateRange",
        label:       "Date range",
        value:       dateRange,
        onChange:    setDateRange,
        placeholder: "Anytime",
      },
    ],
    [searchValue, senderFilter, dateRange, handleSearch]
  );

  return (
    <>
      <TopNav title="Conversations" />
      <div className="global-body">
        <div className="global-body-container">
          <SearchFilterBar filters={filters} onReset={handleReset} />

          <DataTable
            data={STATIC_CONVERSATIONS}
            columns={CONVERSATIONS_COLUMNS}
            renderRow={renderRow}
            onSort={() => {}}
            getSortClass={() => "sort-button"}
            emptyMessage="No conversations found"
            tableClassName="table clientTable"
            colSpan={5}
          />

          <Pagination
            pageSize={pageSize}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </>
  );
};

Conversations.propTypes = {
  userDetails: PropTypes.object.isRequired,
};

export default Conversations;
